import React, { useCallback, useEffect, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { X, ArrowsOut, ArrowsIn, PictureInPicture, MusicNote, DotsSixVertical, ArrowUUpLeft, Play, Pause, CaretLeft, CaretRight, CaretUp, CaretDown, SkipBack, SkipForward, AppWindow, Gear, YoutubeLogo, EyeSlash, Sparkle, Microphone } from "@phosphor-icons/react";
import { useMiniPlayer } from "../context/MiniPlayerContext";
import { useCaptions } from "../context/CaptionsContext";
import useDraggableFAB from "../hooks/useDraggableFAB";
import CastButton from "./CastButton";
import CCToggleButton from "./CCToggleButton";
import BrandMark from "./BrandMark";
import SplashFlash from "./SplashFlash";
import { toast } from "sonner";
import { parseDurationToMs } from "../lib/ytPlayer";

// Floating, draggable, persistent mini-player.
//
// What it does:
//   • Mounts a YouTube IFrame (or HTML5 <video> for direct mp4 /
//     Archive.org sources) at App.js root.
//   • Survives React Router route changes because it sits OUTSIDE the
//     <Routes> tree — so navigating from /movies to /bible/genesis/1
//     does not unmount the iframe, and playback continues.
//   • Long-press to drag, short tap = no-op (the play/pause stays
//     inside the embed's own controls).
//   • Picture-in-Picture button: requests the browser's system-level
//     PiP for HTML5 videos (Cloudinary, Archive.org). For YouTube
//     embeds this is locked down by Google's cross-origin policy —
//     so we hide the PiP button for those and let the user use
//     YouTube's own PiP toggle inside the iframe instead.
//
// Why this is the YouTube-app experience the user asked for:
//   You can be on the Bible reader page, on the Concordance, on a
//   Sermon page, in the Voice panel — and the video keeps playing
//   in the corner the entire time. Tap "ArrowsOut" to expand it
//   back to a full modal on the page you're on.
export default function MiniPlayer() {
  const { item, open, close, fromPath, advanceQueue, previousQueue } = useMiniPlayer();

  // Cross-context audio bus — when ANY other player surface (a new
  // sermon thumbnail, the audio visualizer, a movie modal) broadcasts
  // `hw:audio:start`, close ourselves so two sermon/movie voices
  // never play at the same time. Without this the MiniPlayer kept
  // playing in the corner while the new full-screen player started
  // its own iframe, producing the "two sermon voices overlapping"
  // echo the founder reported from the Home page (2026-02-25).
  useEffect(() => {
    const handler = () => { try { close(); } catch (_) { /* noop */ } };
    window.addEventListener("hw:audio:start", handler);
    return () => window.removeEventListener("hw:audio:start", handler);
  }, [close]);

  // Debug flag — flip on via `?debug=1` in the URL OR `localStorage
  // .setItem("hw_debug","1")` in DevTools. When ON, a small red badge
  // in the player's top-left corner shows the live state (frameless,
  // collapsed, tucked edge, listen-mode, dragging, overlay visible).
  const debugOn = (() => {
    try {
      if (typeof window === "undefined") return false;
      if (window.location.search.includes("debug=1")) return true;
      return window.localStorage.getItem("hw_debug") === "1";
    } catch (_) {
      return false;
    }
  })();
  const navigate = useNavigate();
  // Current route path — used to hide the "Back to [section]" bar
  // when the user is already standing on the page they came from
  // (no point in navigating somewhere you already are).
  const location = useLocation();
  const currentPath = location.pathname;
  const { on: ccOn } = useCaptions();
  const videoRef = useRef(null);
  const advanceQueueRef = useRef(advanceQueue);
  useEffect(() => { advanceQueueRef.current = advanceQueue; }, [advanceQueue]);
  const previousQueueRef = useRef(previousQueue);
  useEffect(() => { previousQueueRef.current = previousQueue; }, [previousQueue]);
  // Track whether the user has tapped to pause the YouTube iframe.
  // postMessage controls the iframe; we just toggle local state for
  // the play/pause icon overlay.
  const [paused, setPaused] = useState(false);
  // FRAMELESS MODE — strips the green chrome (header + footer + back
  // bar) and shows only the video frame with four overlay buttons
  // floating on top: ↩ Back · ⏪ -10s · ▶/⏸ Play/Pause · ✕ Close.
  // The user asked for "the TV screen by itself with the functioning
  // buttons" — this is that mode and it's the DEFAULT now. Users who
  // want the green frame back can tap the 🪟 button in the overlay.
  // Persists per device.
  // Always minimal/frameless per user spec — every mini-player shows
  // ONLY the 5 tap-to-reveal controls, never the framed chrome.
  const [frameless] = useState(true);
  const toggleFrameless = () => {}; // no-op (framed chrome retired)
  // Auto-show the overlay buttons on tap, then fade them after 2.5s
  // so the video isn't permanently cluttered. Same UX every TV remote
  // / Netflix / YouTube uses.
  // Start hidden — icons fade in only when the user taps the video,
  // matching iOS / YouTube PiP "tap to reveal controls" behavior.
  const [overlayVisible, setOverlayVisible] = useState(false);
  // Expanded ("full size") mode — grows the floating widget to a large
  // centered player IN PLACE. Because the iframe/video lives in this
  // App-level component and is never remounted, playback continues
  // from the exact same spot when toggling expand ↔ mini.
  const [expanded, setExpanded] = useState(false);
  const overlayHideTimerRef = useRef(null);
  const showOverlay = () => {
    setOverlayVisible(true);
    if (overlayHideTimerRef.current) clearTimeout(overlayHideTimerRef.current);
    overlayHideTimerRef.current = setTimeout(() => setOverlayVisible(false), 2500);
  };

  // Clear the overlay-hide timer + reset visibility whenever the
  // mini-player opens or closes a track. Without this, a timer queued
  // from a previous track could flip overlay state mid-gesture on
  // the new one (controls flicker glitch per RCA).
  useEffect(() => {
    if (overlayHideTimerRef.current) clearTimeout(overlayHideTimerRef.current);
    setOverlayVisible(false);
    setExpanded(false);
  }, [open, item?.id]);

  // Double-tap-to-skip — Netflix / YouTube-style. Two quick taps on
  // the LEFT half of the video → skip backward 10 s. Two on the RIGHT
  // → skip forward 10 s. Plays a brief flash overlay on the side that
  // got tapped so the user gets visual feedback. We track the last
  // tap timestamp + side; if a second tap lands within 300 ms on the
  // same side we trigger the skip.
  const lastTapRef = useRef({ at: 0, side: null });
  const [flashSide, setFlashSide] = useState(null);
  const flashTimerRef = useRef(null);
  // Double-tap-anywhere-on-the-video → toggle frameless ↔ framed.
  // Works in BOTH modes so the user can flip between "TV only" and
  // "full chrome with all the buttons" without hunting for the 🪟
  // toggle in the toolbar. Same intuition as YouTube's double-tap-
  // to-fullscreen. Threshold tuned to 320 ms so a normal tap is not
  // mistaken for a half-double-tap. We track the tap timestamp on a
  // SEPARATE ref so it does not interfere with the existing
  // double-tap-to-skip logic (which is side-aware: left vs right).
  const lastModeToggleTapRef = useRef(0);
  // Disabled per user spec ("only these buttons, nothing else"): the
  // mini-player now stays in the minimal frameless overlay and never
  // switches to the cluttered framed chrome. Always a no-op.
  const handleStageDoubleTapToToggle = () => false;
  const handleStageTap = (e) => {
    if (!frameless) return;
    showOverlay();
    const rect = e.currentTarget.getBoundingClientRect();
    const x = (e.clientX || (e.touches && e.touches[0]?.clientX) || rect.left + rect.width / 2) - rect.left;
    const side = x < rect.width / 2 ? "left" : "right";
    const now = Date.now();
    const last = lastTapRef.current;
    if (last.side === side && now - last.at < 320) {
      // Second tap on the same side within window → trigger skip.
      skipBy(side === "left" ? -10 : 10);
      setFlashSide(side);
      if (flashTimerRef.current) clearTimeout(flashTimerRef.current);
      flashTimerRef.current = setTimeout(() => setFlashSide(null), 380);
      lastTapRef.current = { at: 0, side: null }; // reset
      return;
    }
    lastTapRef.current = { at: now, side };
  };
  useEffect(() => {
    // Don't auto-show overlay on frameless toggle — icons should ONLY
    // appear when the user taps the video, per user spec.
    return () => {
      if (overlayHideTimerRef.current) clearTimeout(overlayHideTimerRef.current);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [frameless]);
  const [pipSupported, setPipSupported] = useState(false);
  // "Listen mode" — hides the video frame visually but keeps the
  // <audio>/<video> element playing. Combined with Media Session,
  // this gives Spotify-style background audio for our own MP4s when
  // the user switches apps. Disabled for YouTube/Archive iframes
  // because we can't separate their audio track.
  const [listenMode, setListenMode] = useState(false);
  // First-time "you can drag this!" coach mark. Shown once per device
  // when the user opens the mini-player for the first time, then
  // hidden forever (flag stored in localStorage).
  const [showCoach, setShowCoach] = useState(false);
  // Collapsed-to-edge mode. When true, the player tucks itself
  // against the nearest viewport edge and only a small HW-branded
  // tab pokes out — tap the tab to expand it back. Audio/video keep
  // playing the whole time because the iframe/video stays mounted.
  const [collapsed, setCollapsed] = useState(() => {
    if (typeof window === "undefined") return false;
    try { return window.localStorage.getItem("hw:miniplayer:collapsed") === "1"; } catch (_) { return false; }
  });
  const collapseTo = useCallback((next) => {
    setCollapsed(next);
    try { window.localStorage.setItem("hw:miniplayer:collapsed", next ? "1" : "0"); } catch (_) {}
    if (next) {
      try { window.localStorage.setItem("hw:miniplayer:collapse-hint-seen", "1"); } catch (_) {}
      setShowCollapseHint(false);
    }
  }, []);
  // Reset collapsed state when a NEW item starts playing. Stale
  // "tucked into edge" state from a prior session was making fresh
  // tracks open already-hidden, leaving users wondering where the
  // player went — flagged by troubleshoot RCA.
  useEffect(() => {
    if (open && item?.id) {
      collapseTo(false);
    }
  }, [item?.id, open, collapseTo]);
  // One-time "you can hide this to the edge now!" hint. Pulses the
  // collapse button + shows a small floating tooltip pointing at it.
  // Shown the first time an existing user opens the mini-player after
  // the edge-tuck feature shipped, then never again. We hide it
  // automatically after 6 s or when the user collapses for real.
  const [showCollapseHint, setShowCollapseHint] = useState(false);
  useEffect(() => {
    if (!open) return;
    if (typeof window === "undefined") return;
    if (window.localStorage.getItem("hw:miniplayer:coach-seen")) return;
    setShowCoach(true);
    const t = setTimeout(() => {
      setShowCoach(false);
      try { window.localStorage.setItem("hw:miniplayer:coach-seen", "1"); } catch (_) {}
    }, 5000);
    return () => clearTimeout(t);
  }, [open]);
  // Memoized so useDraggableFAB doesn't re-bind all pointer handlers
  // on every render — which was causing drag jumps mid-gesture per
  // troubleshoot RCA.
  const handleSnap = useCallback(({ edge, distance }) => {
    if (distance <= 50) {
      collapseTo(true);
      if (edge === "left") {
        try { window.dispatchEvent(new CustomEvent("hw:miniplayer:tucked-left")); } catch (_) {}
      }
    }
  }, [collapseTo]);

  const fab = useDraggableFAB({
    // v2: resets any saved drag position that covered the verse text.
    storageKey: "hw:fab:miniplayer:v2",
    // Default position needs to clear the BottomNav (Home / Listen /
    // + / Today / You — ≈ 84-96px tall on mobile). At `bottom: 16` the
    // MiniPlayer sat directly behind the nav so the Bible chapter
    // controls were unreachable. `bottom: 110` floats it above the
    // nav with breathing room.
    defaultPosition: { right: 16, bottom: 110 },
    size: 256,
    // Reserve space so the MiniPlayer can never be dragged BACK into
    // the bottom-nav zone or up over the page header. 96px below +
    // 80px above. Updated useDraggableFAB respects this floor/ceiling.
    bottomReserve: 96,
    onSnap: handleSnap,
  });

  // Detect PiP availability — only meaningful for our own <video>
  // elements (YouTube iframes block it).
  useEffect(() => {
    if (typeof document === "undefined") return;
    setPipSupported(!!document.pictureInPictureEnabled);
  }, []);

  // Auto-Picture-in-Picture: when the user navigates to another tab,
  // minimizes Chrome, or opens a different app, automatically pop the
  // floating HTML5 video out into a system-level PiP window so it
  // keeps playing visibly on top of whatever they switch to. Only
  // works for our own <video> sources (Cloudinary, Archive.org direct
  // mp4) — YouTube/Archive iframes are cross-origin so the browser
  // blocks the call. For those we degrade gracefully (no-op).
  useEffect(() => {
    if (typeof document === "undefined") return;
    if (!open || !item) return;
    const onHidden = async () => {
      if (document.visibilityState !== "hidden") return;
      const v = videoRef.current;
      if (!v || v.paused) return;
      if (document.pictureInPictureElement) return; // already in PiP
      try { await v.requestPictureInPicture(); } catch (_) {}
    };
    document.addEventListener("visibilitychange", onHidden);
    return () => document.removeEventListener("visibilitychange", onHidden);
  }, [open, item]);

  // Media Session API — wires the playing HTML5 video into the phone's
  // system media controls (lock screen, notification shade, Bluetooth
  // headphones). When the user switches apps, the audio keeps playing
  // and they can control it without coming back to the browser —
  // exactly the "Spotify in background" UX. Only effective for our
  // own <video> tags; YouTube/Archive iframes are sandboxed.
  useEffect(() => {
    if (typeof navigator === "undefined" || !navigator.mediaSession) return;
    if (!open || !item) return;
    const v = videoRef.current;
    if (!v) return;
    navigator.mediaSession.metadata = new window.MediaMetadata({
      title: item.title || "Holy Words",
      artist: item.subtitle || "Holy Words",
      album: "Holy Words",
      artwork: item.poster
        ? [{ src: item.poster, sizes: "512x512", type: "image/jpeg" }]
        : [
            { src: "/icons/holywords-192.png", sizes: "192x192", type: "image/png" },
            { src: "/icons/holywords-512.png", sizes: "512x512", type: "image/png" },
          ],
    });
    const setupHandlers = () => {
      try { navigator.mediaSession.setActionHandler("play", () => { v.play().catch(() => {}); }); } catch (_) {}
      try { navigator.mediaSession.setActionHandler("pause", () => v.pause()); } catch (_) {}
      try { navigator.mediaSession.setActionHandler("seekto", (e) => { if (Number.isFinite(e.seekTime)) v.currentTime = e.seekTime; }); } catch (_) {}
      try {
        navigator.mediaSession.setActionHandler("seekbackward", (e) => {
          v.currentTime = Math.max(0, v.currentTime - (e.seekOffset || 10));
        });
      } catch (_) {}
      try {
        navigator.mediaSession.setActionHandler("seekforward", (e) => {
          v.currentTime = Math.min(v.duration || 0, v.currentTime + (e.seekOffset || 10));
        });
      } catch (_) {}
      // Lock-screen "next" / "previous" — walk the mini-player queue so
      // the user can skip tracks without unlocking the phone.
      try { navigator.mediaSession.setActionHandler("nexttrack", () => { advanceQueueRef.current && advanceQueueRef.current(); }); } catch (_) {}
      try { navigator.mediaSession.setActionHandler("previoustrack", () => { previousQueueRef.current && previousQueueRef.current(); }); } catch (_) {}
      try { navigator.mediaSession.setActionHandler("stop", () => { v.pause(); }); } catch (_) {}
    };
    setupHandlers();
    // Mirror the element's play/pause into the OS session state and keep
    // the lock-screen scrubber in sync. Without an explicit playbackState
    // some phones drop the session and stop background audio when the
    // screen turns off — this is what keeps it alive.
    const syncPlaying = () => { try { navigator.mediaSession.playbackState = "playing"; } catch (_) {} };
    const syncPaused = () => { try { navigator.mediaSession.playbackState = "paused"; } catch (_) {} };
    const syncPosition = () => {
      if (!navigator.mediaSession.setPositionState) return;
      const dur = v.duration;
      if (!Number.isFinite(dur) || dur <= 0) return;
      try {
        navigator.mediaSession.setPositionState({
          duration: dur,
          playbackRate: v.playbackRate || 1,
          position: Math.min(Math.max(0, v.currentTime || 0), dur),
        });
      } catch (_) {}
    };
    v.addEventListener("play", syncPlaying);
    v.addEventListener("playing", syncPlaying);
    v.addEventListener("pause", syncPaused);
    v.addEventListener("timeupdate", syncPosition);
    v.addEventListener("loadedmetadata", syncPosition);
    if (!v.paused) syncPlaying();
    return () => {
      v.removeEventListener("play", syncPlaying);
      v.removeEventListener("playing", syncPlaying);
      v.removeEventListener("pause", syncPaused);
      v.removeEventListener("timeupdate", syncPosition);
      v.removeEventListener("loadedmetadata", syncPosition);
      try { navigator.mediaSession.metadata = null; } catch (_) {}
      ["play", "pause", "seekto", "seekbackward", "seekforward", "nexttrack", "previoustrack", "stop"].forEach((a) => {
        try { navigator.mediaSession.setActionHandler(a, null); } catch (_) {}
      });
    };
  }, [open, item]);

  // Resume from the position the user was at in the full player.
  // For HTML5 video we wait for metadata then seek; for the YouTube
  // iframe we inject `start=<sec>` into the embed URL (handled
  // further down in the JSX). Archive.org doesn't expose a seek API
  // — we silently no-op there.
  const resumeAt = Number.isFinite(item?.resume_at) ? Math.max(0, item.resume_at) : 0;
  const resumeAppliedRef = useRef(false);
  // Reset the "applied" flag whenever a new media item is loaded so a
  // fresh resume_at can be honored.
  useEffect(() => { resumeAppliedRef.current = false; }, [item?.id]);
  useEffect(() => {
    if (!open || !item) return;
    if (!item.mp4_url) return;
    if (resumeAt <= 1) return;
    const v = videoRef.current;
    if (!v) return;
    const apply = () => {
      if (resumeAppliedRef.current) return;
      try {
        v.currentTime = Math.max(0, resumeAt - 0.5);
        v.play?.().catch(() => {});
        resumeAppliedRef.current = true;
      } catch (_) {}
    };
    if (v.readyState >= 1) {
      apply();
    } else {
      v.addEventListener("loadedmetadata", apply, { once: true });
      v.addEventListener("canplay", apply, { once: true });
      return () => {
        v.removeEventListener("loadedmetadata", apply);
        v.removeEventListener("canplay", apply);
      };
    }
  }, [open, item, resumeAt]);

  // Compute media-type flags BEFORE any early returns / hooks below
  // depend on them. Safe to evaluate even when `item` is null —
  // optional-chaining returns undefined → falsy.
  const isYouTube = !!item?.youtube_id && !item?.mp4_url;
  const isArchive = !!item?.archive_id && !item?.mp4_url;
  const isHtmlVideo = !!item?.mp4_url;

  // Tracks the YouTube iframe's reported `currentTime` (seconds). The
  // YT IFrame API doesn't expose a synchronous getter through cross-
  // origin postMessage — we listen for its `onStateChange` and
  // `infoDelivery` events and cache the latest position so `skipBy`
  // can do RELATIVE seeks instead of jumping to absolute deltaSec.
  //
  // ⚠️ HOOK ORDER — these MUST live above the early-return below.
  // Putting useRef/useEffect after `if (!open) return null` violates
  // Rules of Hooks and crashes the production build on Cloud Build.
  const ytCurrentTimeRef = useRef(0);
  useEffect(() => {
    if (!isYouTube) return;
    // CRITICAL: zero out the cached YouTube currentTime when the
    // track changes. Without this, the next track inherits the
    // previous track's position (e.g. 240s into a 242s song) and
    // any near-end / skipBy logic immediately misfires. Same fix
    // we applied to the full-screen player.
    ytCurrentTimeRef.current = 0;
    let advancedNearEnd = false;
    // Catalog-duration timer: if the item has a known duration (e.g.
    // "4 min" or "45 min"), set an absolute timeout that force-advances
    // the queue once the video should be over. This is the primary
    // continuous-play mechanism for the mini-player — independent of
    // YouTube's postMessage events which can be silent on Vevo embeds.
    const catalogMs = parseDurationToMs(item?.duration);
    let catalogTimer = null;
    if (catalogMs > 0) {
      catalogTimer = setTimeout(() => {
        if (!advancedNearEnd) {
          advancedNearEnd = true;
          advanceQueueRef.current && advanceQueueRef.current();
        }
      }, catalogMs + 1500);  // [HW speed-tune 2026-06-30] was: catalogMs + 4000
    }
    const onMsg = (e) => {
      try {
        const data = typeof e.data === "string" ? JSON.parse(e.data) : e.data;
        if (!data) return;
        // Track currentTime from infoDelivery for seek support.
        if (data.event === "infoDelivery") {
          const t = data?.info?.currentTime;
          if (Number.isFinite(t)) ytCurrentTimeRef.current = t;
          // Near-end detector: advance ~2s BEFORE the end so the next
          // song's iframe (loadVideoById) starts buffering while the
          // tail of the current song is still playing — the buffer
          // overlaps the tail instead of leaving a silent gap after it.
          const dur = data?.info?.duration;
          // [HW speed-tune 2026-06-30] lead increased 2.0 -> 3.0 so the
          // next track starts buffering a touch earlier (snappier hand-off).
          if (Number.isFinite(dur) && dur > 5 && Number.isFinite(t) && t > 0 && t >= dur - 3.0) {
            if (!advancedNearEnd) {
              advancedNearEnd = true;
              advanceQueueRef.current && advanceQueueRef.current();
            }
          }
        }
        // YouTube state 0 = ended.
        if (data.event === "onStateChange" && data.info?.playerState === 0) {
          if (!advancedNearEnd) {
            advancedNearEnd = true;
            advanceQueueRef.current && advanceQueueRef.current();
          }
        }
        // Auto-advance on YouTube errors too.
        if (data.event === "onError") {
          if (!advancedNearEnd) {
            advancedNearEnd = true;
            setTimeout(() => { advanceQueueRef.current && advanceQueueRef.current(); }, 500);  // [HW speed-tune 2026-06-30] was: 1200
          }
        }
      } catch (_) {}
    };
    window.addEventListener("message", onMsg);
    const tick = () => {
      const fr = document.querySelector('[data-testid="mini-player"] iframe');
      if (!fr || !fr.contentWindow) return;
      try {
        fr.contentWindow.postMessage(JSON.stringify({ event: "listening", id: "hw-mini" }), "*");
        fr.contentWindow.postMessage(JSON.stringify({ event: "command", func: "addEventListener", args: ["onStateChange"] }), "*");
      } catch (_) {}
    };
    const t = setTimeout(tick, 800);
    return () => {
      clearTimeout(t);
      if (catalogTimer) clearTimeout(catalogTimer);
      window.removeEventListener("message", onMsg);
    };
  }, [isYouTube, item?.id]);

  // ── Persistent YouTube iframe for gapless-as-possible continuous play ──
  // The mini-player used to advance the queue by letting `item.youtube_id`
  // change, which swapped the <iframe src> and FORCED a full iframe
  // remount: YouTube re-handshakes, re-runs the autoplay-with-sound
  // policy gate (mobile frequently refuses autoplay on a fresh frame so
  // the next song wouldn't start at all), and re-buffers from zero — a
  // multi-second dead-air "pause" between songs. Instead we now pin the
  // iframe URL to the FIRST track and swap subsequent songs IN PLACE via
  // the IFrame API's `loadVideoById` postMessage — same player session,
  // same autoplay grant, just buffer time. Mirrors AudioVisualizerPlayer.
  const ytStableSrcRef = useRef(null);
  const ytLoadedIdRef = useRef(null);
  // Drop the pinned src whenever we leave YouTube (player closed, or the
  // current track is an mp4 / archive item) so the next YouTube session
  // starts fresh on its own first track.
  useEffect(() => {
    if (!isYouTube) {
      ytStableSrcRef.current = null;
      ytLoadedIdRef.current = null;
    }
  }, [isYouTube]);
  // Swap the playing video in-place when the queue advances to another
  // YouTube track while the iframe is already mounted.
  useEffect(() => {
    if (!isYouTube) return;
    const ytId = item?.youtube_id;
    if (!ytId) return;
    // First track is loaded by the pinned embed URL itself — recording
    // it here (without a postMessage) avoids a restart-from-0 flash.
    if (ytLoadedIdRef.current === null) { ytLoadedIdRef.current = ytId; return; }
    if (ytLoadedIdRef.current === ytId) return;
    const swap = setTimeout(() => {
      const fr = document.querySelector('[data-testid="mini-player"] iframe');
      if (!fr || !fr.contentWindow) return;
      try {
        fr.contentWindow.postMessage(JSON.stringify({
          event: "command",
          func: "loadVideoById",
          args: [{ videoId: ytId, startSeconds: 0 }],
        }), "*");
        ytLoadedIdRef.current = ytId;
      } catch (_) {}
    }, 60);
    return () => clearTimeout(swap);
  }, [isYouTube, item?.youtube_id]);

  // One-time hint: warn the user the FIRST time they start a YouTube
  // track that closing the screen will pause playback. YouTube
  // iframes are paused by Chrome/Safari the moment the tab
  // backgrounds — that's a YouTube policy, not our code. We point
  // them to the new "Open in YouTube" button (red icon, top-right of
  // the player) which launches the native app where background play
  // works. Mobile-only — desktop browsers don't have this issue, so
  // we don't pester desktop users with a notice they can ignore.
  useEffect(() => {
    if (!isYouTube || !item?.youtube_id) return;
    if (typeof window === "undefined") return;
    try {
      const isMobile = /Mobi|Android|iPhone|iPad|iPod/i.test(navigator.userAgent || "");
      if (!isMobile) return;
      const SEEN_KEY = "hw:hint:youtube-background-v1";
      if (window.localStorage.getItem(SEEN_KEY)) return;
      window.localStorage.setItem(SEEN_KEY, "1");
      // Slight delay so the toast doesn't compete with the player's
      // own load animation.
      const handle = setTimeout(() => {
        toast(
          "Heads up — YouTube tracks pause when your screen turns off. Tap the red YouTube icon on the player to open in the YouTube app for background play.",
          { duration: 8000 }
        );
      }, 1500);
      return () => clearTimeout(handle);
    } catch (_) {}
  }, [isYouTube, item?.youtube_id]);

  if (!open || !item) return null;

  // Pinned YouTube embed URL. We build it from the FIRST track and then
  // never let it change — subsequent songs are swapped inside the same
  // iframe via the loadVideoById effect above, so the <iframe src> stays
  // constant and React never re-mounts (re-mounting = re-buffer + lost
  // autoplay grant = the pause between songs). `enablejsapi=1` + `origin`
  // are required for the IFrame API postMessage commands to be accepted.
  const ytOrigin = typeof window !== "undefined" ? window.location.origin : "";
  const ytDirectSrc = item.youtube_id
    ? `https://www.youtube.com/embed/${item.youtube_id}?autoplay=1&playsinline=1&modestbranding=1&rel=0&enablejsapi=1&start=${resumeAt > 1 ? Math.floor(resumeAt) : 5}${ccOn ? "&cc_load_policy=1&cc_lang_pref=en" : ""}&origin=${encodeURIComponent(ytOrigin)}`
    : null;
  if (isYouTube && !ytStableSrcRef.current && ytDirectSrc) {
    ytStableSrcRef.current = ytDirectSrc;
  }
  const ytPlayerSrc = ytStableSrcRef.current || ytDirectSrc;

  // Document Picture-in-Picture — newer browser API (Chrome 116+ on
  // Android & desktop) that pops ARBITRARY HTML into a floating
  // window that survives leaving the app. Works for YouTube
  // iframes too, unlike the older PiP which only worked on <video>.
  const docPipSupported = typeof window !== "undefined" && "documentPictureInPicture" in window;

  const requestPiP = async () => {
    const v = videoRef.current;
    if (!v) return;
    try {
      if (document.pictureInPictureElement) {
        await document.exitPictureInPicture();
      } else {
        await v.requestPictureInPicture();
      }
    } catch (_) {
      /* user denied or browser refused — silently no-op */
    }
  };

  // Unified play/pause toggle that works for BOTH the YouTube /
  // Archive iframes (via postMessage) and our own HTML5 <video>
  // (via the videoRef). Called from the frameless overlay's center
  // play/pause button.
  const togglePlayPause = () => {
    if (isHtmlVideo) {
      const v = videoRef.current;
      if (!v) return;
      if (v.paused) {
        v.play().catch(() => {});
        setPaused(false);
      } else {
        v.pause();
        setPaused(true);
      }
      return;
    }
    const fr = document.querySelector('[data-testid="mini-player"] iframe');
    if (!fr || !fr.contentWindow) return;
    try {
      fr.contentWindow.postMessage(
        JSON.stringify({ event: "command", func: paused ? "playVideo" : "pauseVideo", args: [] }),
        "*"
      );
      setPaused((v) => !v);
    } catch (_) {}
  };

  // Skip ± 10 seconds. HTML5 video: seek via currentTime. YouTube:
  // compute next absolute timestamp from cached current time then
  // seekTo it. Archive.org iframes don't expose a postMessage seek
  // API, so we silently no-op.
  const skipBy = (deltaSec) => {
    if (isHtmlVideo) {
      const v = videoRef.current;
      if (!v) return;
      v.currentTime = Math.max(0, Math.min((v.duration || Infinity), v.currentTime + deltaSec));
      return;
    }
    if (isYouTube) {
      const fr = document.querySelector('[data-testid="mini-player"] iframe');
      if (!fr || !fr.contentWindow) return;
      const next = Math.max(0, (ytCurrentTimeRef.current || 0) + deltaSec);
      try {
        fr.contentWindow.postMessage(
          JSON.stringify({ event: "command", func: "seekTo", args: [next, true] }),
          "*"
        );
        // Optimistically update so the next press is relative to the
        // intended seek (the YT player will overwrite when it reports
        // back via infoDelivery).
        ytCurrentTimeRef.current = next;
      } catch (_) {}
    }
  };

  // Pop the video into a floating system-level window via Document
  // PiP. We deliberately DON'T clone host-page stylesheets — reading
  // external sheets throws SecurityError on most browsers and copying
  // every Tailwind class inflates the PiP window pointlessly. Inline
  // styles only.
  //
  // The PiP window stays on top of OTHER apps on Android (Chrome PWA /
  // TWA) and desktop, so the user can switch to Gmail / WhatsApp /
  // anywhere — Holy Words keeps playing in the floating window. We
  // add a "Return to Holy Words" overlay button that focuses the
  // parent tab so they can hop back with one tap.
  const requestDocPiP = async () => {
    if (!docPipSupported) return;
    try {
      const pipWin = await window.documentPictureInPicture.requestWindow({
        width: 440, height: 270,
      });
      pipWin.document.documentElement.style.margin = "0";
      pipWin.document.documentElement.style.background = "#000";
      pipWin.document.body.style.margin = "0";
      pipWin.document.body.style.background = "#000";
      pipWin.document.body.style.height = "100vh";
      pipWin.document.body.style.overflow = "hidden";
      const titleEl = pipWin.document.createElement("title");
      titleEl.textContent = item.title || "Holy Words";
      pipWin.document.head.appendChild(titleEl);

      const wrap = pipWin.document.createElement("div");
      wrap.style.cssText = "width:100%;height:100%;background:#000;display:flex;align-items:center;justify-content:center;position:relative;";
      if (item.youtube_id) {
        const iframe = pipWin.document.createElement("iframe");
        iframe.src = `https://www.youtube.com/embed/${item.youtube_id}?autoplay=1&playsinline=1&modestbranding=1&rel=0&start=${resumeAt > 1 ? Math.floor(resumeAt) : 5}${ccOn ? "&cc_load_policy=1&cc_lang_pref=en" : ""}`;
        iframe.setAttribute("allow", "autoplay; encrypted-media; picture-in-picture");
        iframe.setAttribute("allowfullscreen", "true");
        iframe.style.cssText = "border:0;width:100%;height:100%;display:block;";
        wrap.appendChild(iframe);
      } else if (item.archive_id) {
        const iframe = pipWin.document.createElement("iframe");
        iframe.src = `https://archive.org/embed/${item.archive_id}?autoplay=1&playsinline=1`;
        iframe.setAttribute("allow", "autoplay; encrypted-media; picture-in-picture");
        iframe.setAttribute("allowfullscreen", "true");
        iframe.style.cssText = "border:0;width:100%;height:100%;display:block;";
        wrap.appendChild(iframe);
      } else if (item.mp4_url) {
        const video = pipWin.document.createElement("video");
        video.src = item.mp4_url;
        video.controls = true;
        video.autoplay = true;
        video.playsInline = true;
        if (resumeAt > 1) {
          const seekOnReady = () => {
            try { video.currentTime = Math.max(0, resumeAt - 0.5); } catch (_) {}
            video.removeEventListener("loadedmetadata", seekOnReady);
          };
          video.addEventListener("loadedmetadata", seekOnReady);
        }
        video.style.cssText = "width:100%;height:100%;background:#000;display:block;";
        wrap.appendChild(video);
      }
      // "↩ Holy Words" hop-back banner. Tapping focuses the parent
      // browser tab (Chrome supports window.focus() across PiP) and
      // closes the floating window. On Android/TWA this brings the
      // Holy Words app back to the foreground so the user can browse
      // the rest of the catalog.
      const banner = pipWin.document.createElement("button");
      banner.type = "button";
      banner.textContent = "↩  Tap to return to Holy Words";
      banner.style.cssText = "position:absolute;top:8px;left:50%;transform:translateX(-50%);padding:8px 14px;border-radius:999px;background:rgba(204,89,47,0.95);color:#FAF7F2;font-family:-apple-system,system-ui,Segoe UI,Roboto,sans-serif;font-size:11px;font-weight:700;letter-spacing:0.18em;text-transform:uppercase;border:0;cursor:pointer;box-shadow:0 4px 12px rgba(0,0,0,0.4);backdrop-filter:blur(8px);z-index:10;";
      banner.onclick = () => {
        try { window.focus(); } catch (_) {}
        try { pipWin.close(); } catch (_) {}
      };
      wrap.appendChild(banner);
      pipWin.document.body.appendChild(wrap);
    } catch (_) {
      /* user closed dialog or unsupported — silent */
    }
  };

  // Unified "Pop Out of Holy Words" entry point used by the prominent
  // button in the frameless overlay. Picks the BEST PiP method for
  // the current media type:
  //   • HTML5 video (Cloudinary / direct MP4) → native Video PiP
  //     (`requestPictureInPicture`). This is the most widely supported
  //     and works on iOS Safari too.
  //   • YouTube / Archive iframe → Document PiP if the browser
  //     supports it (Chrome 116+). On unsupported browsers we fall
  //     back to opening the video in a new tab so the user can still
  //     keep watching while they switch apps.
  // After popping out, the in-app mini-player closes so the user
  // doesn't see two copies of the same video on the same screen.
  const popOutOfApp = async () => {
    try {
      if (isHtmlVideo) {
        const v = videoRef.current;
        if (v) {
          try {
            await v.requestPictureInPicture();
            return;
          } catch (_) { /* fall through to docPiP */ }
        }
      }
      if (docPipSupported) {
        await requestDocPiP();
        return;
      }
      // Founder rule: never leave the app. If neither native PiP
      // nor document PiP is supported, we simply notify the user
      // and keep them in-app. Previously this opened YouTube/archive
      // in a new tab; that's now gone.
      try { toast.info("Floating window isn't supported on this browser."); } catch (_) {}
    } catch (_) {}
  };

  // Pick which of the FOUR edges the FAB should tuck against when
  // collapsed. We measure the LIVE bounding rect of the widget and
  // pick whichever edge of the viewport its CENTER sits closest to.
  // The user can park the player anywhere on screen — including the
  // top — so they can tuck it up, down, left, or right with one tap.
  const tuckedEdge = (() => {
    if (typeof window === "undefined") return "right";
    const node = typeof document !== "undefined" ? document.querySelector('[data-testid="mini-player"]') : null;
    // Fall back to style-based hint until the ref is hot. Once we
    // have a real bounding box (post-mount + post-collapse), we use
    // it because it accounts for the translateY/translateX of the
    // collapsed state. The COLLAPSED transform shouldn't matter
    // because we compute this BEFORE applying the transform — but
    // for the very first render we look at fab.style as a hint.
    let rect = null;
    try { rect = node && node.getBoundingClientRect ? node.getBoundingClientRect() : null; } catch (_) {}
    const vw = window.innerWidth;
    const vh = window.innerHeight;
    if (rect) {
      // While collapsed, the rect includes our translate offset, so
      // we cannot trust it — we'd "see" the player off-screen and
      // pick the wrong edge. Use the un-transformed reference: the
      // raw fab.style position (left/right/top/bottom in px) tells
      // us where the user PARKED it. When the user is dragging or
      // we are expanded, the rect is accurate.
      if (!collapsed) {
        const cx = rect.left + rect.width / 2;
        const cy = rect.top + rect.height / 2;
        const dLeft = cx;
        const dRight = vw - cx;
        const dTop = cy;
        const dBottom = vh - cy;
        const min = Math.min(dLeft, dRight, dTop, dBottom);
        if (min === dLeft) return "left";
        if (min === dRight) return "right";
        if (min === dTop) return "top";
        return "bottom";
      }
    }
    // Fallback: derive from the style hints. The drag hook stores
    // exactly ONE of {left, right} and ONE of {top, bottom} after a
    // snap-to-edge release. The one with the smaller value is the
    // dominant axis.
    const s = fab.style || {};
    const candidates = [];
    if (typeof s.left === "number") candidates.push(["left", s.left]);
    if (typeof s.right === "number") candidates.push(["right", s.right]);
    if (typeof s.top === "number") candidates.push(["top", s.top]);
    if (typeof s.bottom === "number") candidates.push(["bottom", s.bottom]);
    if (candidates.length === 0) return "right";
    candidates.sort((a, b) => a[1] - b[1]);
    return candidates[0][0];
  })();

  // When collapsed, slide the whole widget so only a ~44px tab peeks
  // out from the edge. We translate along the appropriate axis based
  // on which edge it's nearest. Iframe / video DOM stays mounted so
  // playback continues even though the widget is mostly off-screen.
  const collapsedTransform = (() => {
    if (!collapsed) return undefined;
    if (tuckedEdge === "left") return "translateX(calc(-100% + 44px))";
    if (tuckedEdge === "right") return "translateX(calc(100% - 44px))";
    if (tuckedEdge === "top") return "translateY(calc(-100% + 44px))";
    return "translateY(calc(100% - 44px))";
  })();

  // If the user explicitly collapsed the player, surface a small
  // brand-mark tab on the visible strip. Tap → expand. We deliberately
  // STOP the drag-from-tab gesture from initiating a drag so the user
  // can tap the tab without triggering the FAB drag threshold.
  const expand = (e) => {
    e?.stopPropagation();
    collapseTo(false);
  };

  return (
    <div
      ref={fab.ref}
      style={expanded
        ? { position: "fixed", left: "50%", top: "50%", transform: "translate(-50%, -50%)", width: "min(96vw, 880px)", zIndex: 60, transition: "all 240ms cubic-bezier(0.4,0,0.2,1)" }
        : { ...fab.style, transform: collapsedTransform, transition: fab.dragging ? "none" : "all 280ms cubic-bezier(0.4,0,0.2,1)" }}
      data-testid="mini-player"
      data-expanded={expanded ? "true" : "false"}
      data-collapsed={collapsed ? "true" : "false"}
      className={`z-[40] ${expanded ? "w-[96vw] max-w-[880px] cursor-default" : `w-[200px] sm:w-[240px] ${frameless ? "" : "md:w-[260px] lg:w-[300px]"} cursor-grab active:cursor-grabbing`} rounded-2xl overflow-hidden shadow-2xl touch-none select-none relative ${
        frameless
          ? "bg-transparent border-0"
          : `hw-player-deep border ${fab.dragging ? "border-terracotta/40" : "border-cream/15"}`
      } ${fab.dragging ? "ring-4 ring-terracotta/60 scale-[1.02]" : ""} ${showCoach ? "animate-pulse" : ""}`}
    >
      {/* Dev-only debug overlay. Activate via ?debug=1 in the URL or
          `localStorage.setItem("hw_debug","1")` in DevTools. Prints
          current MiniPlayer state in the corner so the next time
          something glitches we can diagnose in 5 seconds without
          having to spelunk through the React tree. */}
      {debugOn && (
        <div
          data-testid="mini-player-debug"
          className="absolute top-0 left-0 z-[100] px-1.5 py-0.5 text-[8px] leading-tight font-mono tracking-wider text-cream bg-red-700/90 rounded-br-md pointer-events-none"
          style={{ maxWidth: "100%" }}
        >
          {`f=${frameless ? "Y" : "N"} c=${collapsed ? "Y" : "N"} e=${tuckedEdge || "-"} l=${listenMode ? "Y" : "N"} d=${fab.dragging ? "Y" : "N"} o=${overlayVisible ? "Y" : "N"}`}
        </div>
      )}
      {/* Collapsed-state tab — the HW brand mark + chevron poking out
          of the edge of the screen. Sits on top of the still-rendered
          mini-player content (audio keeps playing underneath). Same
          DOM, just visually shifted off-screen by the transform above.
          The tab orients itself based on which edge it's tucked into:
            • left/right edge → vertical pill, 44 × 80
            • top/bottom edge → horizontal pill, 80 × 44 */}
      {collapsed && (
        <button
          type="button"
          onPointerDown={(e) => {
            // Track this pointer so we can detect a drag-OUT-of-edge
            // gesture without losing the quick-tap-to-expand affordance.
            // If the user pulls their finger ≥25px AWAY from the edge
            // we're tucked into, expand. Otherwise let the click fire
            // and we expand on tap.
            e.stopPropagation();
            const startX = e.clientX;
            const startY = e.clientY;
            let expandedByDrag = false;
            const move = (ev) => {
              if (expandedByDrag) return;
              const dx = ev.clientX - startX;
              const dy = ev.clientY - startY;
              const pulled =
                (tuckedEdge === "right" && dx < -25) ||
                (tuckedEdge === "left"  && dx >  25) ||
                (tuckedEdge === "bottom" && dy < -25) ||
                (tuckedEdge === "top"    && dy >  25);
              if (pulled) {
                expandedByDrag = true;
                collapseTo(false);
              }
            };
            const up = () => {
              window.removeEventListener("pointermove", move);
              window.removeEventListener("pointerup", up);
              window.removeEventListener("pointercancel", up);
            };
            window.addEventListener("pointermove", move);
            window.addEventListener("pointerup", up);
            window.addEventListener("pointercancel", up);
          }}
          onClick={(e) => {
            // Tap-to-expand. Skipped automatically if a drag-out already
            // ran (collapsed state will be false by now).
            e.stopPropagation();
            if (collapsed) collapseTo(false);
          }}
          onMouseDown={(e) => e.stopPropagation()}
          onTouchStart={(e) => e.stopPropagation()}
          data-testid="mini-player-expand-tab"
          data-no-drag="1"
          aria-label="Pull out or tap to expand mini player"
          title="Pull out or tap to expand"
          style={{ touchAction: "manipulation" }}
          className={`absolute grid place-items-center bg-forest/95 backdrop-blur border-2 border-terracotta/70 shadow-2xl z-30 cursor-pointer hover:bg-forest active:scale-95 transition-all ${
            tuckedEdge === "left"
              ? "top-1/2 -translate-y-1/2 right-0 w-7 h-16 rounded-none border-l-0"
              : tuckedEdge === "right"
                ? "top-1/2 -translate-y-1/2 left-0 w-7 h-16 rounded-none border-r-0"
                : tuckedEdge === "top"
                  ? "left-1/2 -translate-x-1/2 bottom-0 w-16 h-7 rounded-none border-t-0"
                  : "left-1/2 -translate-x-1/2 top-0 w-16 h-7 rounded-none border-b-0"
          }`}
        >
          <span className={`flex items-center gap-0.5 pointer-events-none ${tuckedEdge === "top" || tuckedEdge === "bottom" ? "flex-row" : "flex-col"}`}>
            <BrandMark size={16} />
            {tuckedEdge === "left" && <CaretRight size={10} weight="bold" className="text-cream/90 animate-pulse" />}
            {tuckedEdge === "right" && <CaretLeft size={10} weight="bold" className="text-cream/90 animate-pulse" />}
            {tuckedEdge === "top" && <CaretDown size={10} weight="bold" className="text-cream/90 animate-pulse" />}
            {tuckedEdge === "bottom" && <CaretUp size={10} weight="bold" className="text-cream/90 animate-pulse" />}
          </span>
        </button>
      )}
      {/* First-time coach mark — fades after 5s so the rest of the
          time the widget is unobtrusive. */}
      {showCoach && !collapsed && (
        <div className="absolute -top-9 right-0 left-0 mx-auto w-fit px-3 py-1.5 text-terracotta hover:text-terracotta/80 text-[10px] tracking-[0.18em] uppercase font-bold shadow-xl animate-bounce pointer-events-none">
          ↓ Hold &amp; drag to move
        </div>
      )}
      {/* Header — title + close. The drag handle. Made tall and
          fat-fingered so mobile users grab it easily.

          Hidden entirely in `frameless` mode — the user explicitly
          asked for "just the TV screen with floating buttons", so the
          green chrome and the back-to-section pill move out of the
          way. The overlay buttons rendered ON TOP of the video later
          in this component cover the same functions. */}
      {!frameless && (
      <div className="flex items-center justify-between gap-1 px-2 py-2 bg-forest text-cream text-[10px] uppercase tracking-[0.18em] font-semibold cursor-grab active:cursor-grabbing">
        <DotsSixVertical size={14} weight="bold" className="opacity-90 shrink-0" />
        <span className="w-1.5 h-1.5 rounded-full bg-terracotta shrink-0 animate-pulse" />
        <span className="flex-1 truncate" title={item.title}>{item.title || "Now playing"}</span>
        {/* Return arrow — takes the user back to the page they opened
            the player from (Movies / Videos / Cartoons / Gospel /
            wherever) so they can pick up right where they left off.
            Made into a labeled pill (instead of an icon-only 24px
            button) because users couldn't find it. The label auto-
            derives from the path so it reads "Back to Movies" /
            "Back to Videos" / etc. */}
        {fromPath && (
          <button
            type="button"
            onPointerDown={(e) => {
              // Use pointerdown (not click) so a single touch on
              // mobile fires INSTANTLY without waiting for the 300ms
              // click delay, and survives the drag-handler's
              // touch-cancel. data-no-drag also keeps the parent's
              // useDraggableFAB hook from claiming this gesture.
              e.stopPropagation();
              e.preventDefault();
              if (currentPath === fromPath.split("?")[0]) {
                try { window.scrollTo({ top: 0, behavior: "smooth" }); } catch (_) {}
              } else {
                navigate(fromPath);
              }
            }}
            onClick={(e) => { e.stopPropagation(); }}
            data-testid="mini-player-return"
            data-no-drag="1"
            aria-label={`Back to ${labelForPath(fromPath)}`}
            title={`Back to ${labelForPath(fromPath)} — return to where you were watching`}
            className="inline-flex items-center gap-1 px-2 h-7 rounded-full bg-terracotta hover:bg-terracotta-600 active:scale-95 text-cream text-[10px] font-bold tracking-wider uppercase shadow-md shrink-0"
            style={{ touchAction: "manipulation" }}
          >
            <ArrowUUpLeft size={11} weight="bold" />
            <span className="max-w-[64px] truncate">{labelForPath(fromPath)}</span>
          </button>
        )}
        {isHtmlVideo && (
          <button
            type="button"
            onClick={(e) => { e.stopPropagation(); setListenMode((v) => !v); }}
            data-testid="mini-player-listen"
            aria-label={listenMode ? "Show video" : "Listen mode — audio only"}
            title={listenMode ? "Show video" : "Listen mode (audio only — keeps playing when you switch apps)"}
            className={`w-6 h-6 rounded-full grid place-items-center transition-all ${listenMode ? "bg-terracotta text-cream" : "bg-cream/15 hover:bg-cream/30"}`}
          >
            <MusicNote size={11} weight="bold" />
          </button>
        )}
        <button
          type="button"
          onClick={(e) => { e.stopPropagation(); window.dispatchEvent(new CustomEvent("holywords:open-ai")); }}
          data-testid="mini-player-ai"
          aria-label="Ask AI"
          title="Ask AI"
          className="w-6 h-6 rounded-full bg-cream/15 hover:bg-cream/30 grid place-items-center transition-all text-[#E7C47C]"
        >
          <Sparkle size={11} weight="fill" />
        </button>
        <button
          type="button"
          onClick={(e) => { e.stopPropagation(); window.dispatchEvent(new CustomEvent("holywords:open-voice")); }}
          data-testid="mini-player-voice"
          aria-label="Voice commands"
          title="Voice commands"
          className="w-6 h-6 rounded-full bg-cream/15 hover:bg-cream/30 grid place-items-center transition-all text-terracotta"
        >
          <Microphone size={11} weight="fill" />
        </button>
        {pipSupported && isHtmlVideo && (
          <button
            type="button"
            onClick={(e) => { e.stopPropagation(); requestPiP(); }}
            data-testid="mini-player-pip"
            aria-label="Picture in picture"
            title="Pop out into a system-level floating window"
            className="w-6 h-6 rounded-full bg-cream/15 hover:bg-cream/30 grid place-items-center transition-all"
          >
            <PictureInPicture size={11} weight="bold" />
          </button>
        )}
        {docPipSupported && !isHtmlVideo && (
          <button
            type="button"
            onClick={(e) => { e.stopPropagation(); requestDocPiP(); }}
            data-testid="mini-player-doc-pip"
            aria-label="Pop out window"
            title="Pop into a floating window — keeps playing when you leave the app"
            className="w-6 h-6 rounded-full bg-terracotta/80 hover:bg-terracotta grid place-items-center transition-all"
          >
            <PictureInPicture size={11} weight="bold" />
          </button>
        )}
        {isHtmlVideo && (
          <CastButton
            media={{
              url: item.mp4_url,
              contentType: "video/mp4",
              title: item.title,
              subtitle: item.subtitle,
              poster: item.poster,
            }}
            videoRef={videoRef}
            testid="mini-player-cast"
          />
        )}
        <CCToggleButton variant="dark" testid="mini-player-cc" className="!w-6 !h-6" />
        {/* Open in YouTube app — escape hatch for background playback.
            Mobile browsers (Chrome on Android, Safari on iOS) pause
            YouTube iframes the moment the screen turns off. This
            button launches the user's native YouTube app instead,
            where background play actually works (free for ads-on,
            seamless for YouTube Premium subscribers). The browser
            iframe limitation is YouTube's intentional policy to push
            people to the app — we surface a one-tap workaround
            rather than fight it. The button hides automatically when
            the current track is not a YouTube embed. */}
        {isYouTube && item?.youtube_id && (
          <a
            href={`https://www.youtube.com/watch?v=${item.youtube_id}${resumeAt > 1 ? `&t=${Math.floor(resumeAt)}s` : ""}`}
            target="_blank"
            rel="noopener noreferrer"
            onClick={(e) => { e.stopPropagation(); }}
            data-testid="mini-player-open-youtube"
            aria-label="Open in YouTube app for background play"
            title="Open in YouTube app — keeps playing with your screen off"
            className="inline-flex items-center justify-center w-6 h-6 rounded-full bg-red-600/90 hover:bg-red-600 text-cream shadow-md transition-all"
          >
            <YoutubeLogo size={11} weight="fill" />
          </a>
        )}
        {/* Frameless toggle — strips the green box leaving just the
            video frame. ONE button, labeled clearly so users don't
            have to guess. State persists globally via localStorage so
            flipping it here also flips every other player + every
            page (sermons / movies / songs / kids). User explicitly
            asked: "make it simple, one button". */}
        <button
          type="button"
          onPointerDown={(e) => { e.stopPropagation(); e.preventDefault(); toggleFrameless(); }}
          onClick={(e) => { e.stopPropagation(); }}
          data-testid="mini-player-frameless"
          data-no-drag="1"
          aria-label={frameless ? "Show green frame" : "Frameless — just the screen"}
          title={frameless ? "Bring the green frame back" : "Frameless — show just the screen"}
          className={`inline-flex items-center justify-center w-6 h-6 rounded-full ${frameless ? "bg-cream/20 hover:bg-cream/30" : "bg-terracotta hover:bg-terracotta-600 ring-2 ring-terracotta/40"} text-cream shadow-md transition-all`}
          style={{ touchAction: "manipulation" }}
        >
          <AppWindow size={11} weight="bold" />
        </button>
        <button
          type="button"
          onClick={(e) => { e.stopPropagation(); close(); }}
          data-testid="mini-player-close"
          aria-label="Close mini player"
          className="w-6 h-6 rounded-full bg-cream/15 hover:bg-terracotta hover:text-cream grid place-items-center transition-all"
        >
          <X size={11} weight="bold" />
        </button>
      </div>
      )}

      {/* Stage. We pad the iframe with a fat dark bezel so a finger
          has a generous draggable border to grab on mobile. The iframe
          itself still receives taps for YouTube's controls. In
          frameless mode the bezel + inner rounded mask disappear and
          the video sits flush in a single rounded card with overlay
          buttons. */}
      <div
        onClick={(e) => {
          // Try double-tap-to-toggle first. If a toggle fired, we
          // return early so the overlay-show side effect does not
          // run on the same tap. Otherwise, surface the overlay so
          // the floating buttons reappear after fading.
          if (handleStageDoubleTapToToggle(e)) return;
          if (frameless) showOverlay();
        }}
        className={`aspect-video hw-player-deep relative ${frameless ? "rounded-2xl overflow-hidden" : "px-2.5 pb-2.5"} ${listenMode && isHtmlVideo ? "hidden" : ""}`}
      >
        <div className={frameless ? "absolute inset-0 overflow-hidden rounded-2xl hw-player-deep" : "absolute inset-2.5 overflow-hidden rounded-lg hw-player-deep"}>
        {isYouTube && (
          <iframe
            title={item.title}
            src={ytPlayerSrc}
            className="absolute inset-0 w-full h-full pointer-events-none"
            allow="autoplay; encrypted-media; picture-in-picture"
            allowFullScreen
          />
        )}
        {isArchive && (
          <iframe
            title={item.title}
            src={`https://archive.org/embed/${item.archive_id}?autoplay=1&playsinline=1`}
            className="absolute inset-0 w-full h-full pointer-events-none"
            allow="autoplay; encrypted-media; picture-in-picture"
            allowFullScreen
          />
        )}
        {isHtmlVideo && (
          <>
            {/* Poster fills the mini-player stage; the ACTUAL playback runs
                through a persistent HTML5 <audio> element (below), NOT a
                <video>. Mobile browsers SUSPEND <video> when the screen
                turns off, but keep <audio> playing — this is what makes
                background / lock-screen playback work. */}
            {item.poster ? (
              <img src={item.poster} alt={item.title} className="absolute inset-0 w-full h-full object-cover" />
            ) : (
              <div className="absolute inset-0 grid place-items-center bg-gradient-to-br from-forest to-forest/70">
                <MusicNote size={40} weight="duotone" className="text-cream/70" />
              </div>
            )}
            <audio
              ref={videoRef}
              src={item.mp4_url}
              autoPlay
              preload="auto"
              onEnded={() => { advanceQueueRef.current && advanceQueueRef.current(); }}
              className="absolute bottom-0 left-0 right-0 w-full"
              controls
            />
          </>
        )}
        {/* Holy Words brand pre-roll — same 7s splash as the full-screen
            players. Covers the source (YouTube/Archive) intro + logo and
            promotes Holy Words for the first ~7s of EVERY video / movie /
            song that plays in the floating mini-player. Re-fires when the
            queue advances (keyed on item.id). pointer-events-none so it
            never blocks the player controls underneath. */}
        {item?.id && (
          <SplashFlash itemId={`mini-${item.id}`} sub={item.title} />
        )}
        {/* Transparent drag-overlay over iframe-based players. We do
            NOT cover HTML5 <video> because its native controls
            (play/pause, seek bar, fullscreen) need touch access —
            the header, footer, and 10px bezel around the video are
            still draggable for those. On iframe players the iframe
            swallows touches anyway, so we replace it with a drag
            overlay + tap-to-toggle via postMessage.

            We rely on NATIVE event bubbling (pointerdown bubbles
            from this child div up to the parent FAB element where
            the drag hook is listening). Earlier versions synthesized
            and re-dispatched a PointerEvent which actually broke
            Playwright drag tests because the synthetic copy could
            short-circuit the natural bubble path. */}
        {!isHtmlVideo && (
          <div
            className={`absolute inset-0 z-10 grid place-items-center group/play ${collapsed ? "pointer-events-none" : ""}`}
            data-testid="mini-player-drag-overlay"
            // touch-action:none is INHERITED from the FAB but some
            // Android Chrome builds (TWA wrappers, older Chrome) don't
            // propagate it to absolutely-positioned children. Set it
            // explicitly so a finger-down on this overlay can NEVER
            // be hijacked by the browser's gesture detector.
            style={{ touchAction: "none", WebkitUserSelect: "none", userSelect: "none" }}
            onClick={(e) => {
              // Only treat as tap-to-toggle if the drag hook did NOT
              // claim this gesture. The hook flips `fab.dragging`
              // true only when it crosses the THRESHOLD, so a pure
              // tap (no drag) leaves it false.
              if (fab.dragging) return;
              if (handleStageDoubleTapToToggle(e)) return;
              e.stopPropagation();
              // FIRST tap = reveal the controls (Return / rewind / play /
              // forward / close) WITHOUT pausing. Users kept pausing the
              // video when they really meant to tap Return. Only once the
              // overlay is already showing does a tap on the bare video
              // toggle play/pause.
              if (!overlayVisible) { showOverlay(); return; }
              const fr = document.querySelector('[data-testid="mini-player"] iframe');
              if (!fr || !fr.contentWindow) return;
              try {
                fr.contentWindow.postMessage(
                  JSON.stringify({ event: "command", func: paused ? "playVideo" : "pauseVideo", args: [] }),
                  "*"
                );
                setPaused((v) => !v);
              } catch (_) {}
            }}
            aria-label={paused ? "Play" : "Pause"}
            role="button"
          >
            {/* Subtle play/pause hint that fades in on hover/focus so
                the overlay's purpose is discoverable but never noisy. */}
            <div className="w-12 h-12 rounded-full bg-ink/0 group-hover/play:bg-ink/60 grid place-items-center transition-all opacity-0 group-hover/play:opacity-100">
              {paused ? <Play size={20} weight="fill" className="text-cream" /> : <Pause size={20} weight="fill" className="text-cream" />}
            </div>
          </div>
        )}

        {/* FRAMELESS overlay buttons — only render when in frameless
            mode. Four functions floating over the video:
              ↩ Return to the section the player came from
              ⏪ Skip back 10s
              ▶/⏸ Play / Pause
              ✕ Close the player

            All buttons live on translucent dark pills so they read on
            both bright and dark video frames. They auto-fade after
            ~2.5s of inactivity (set by `showOverlay`); a tap anywhere
            on the video brings them back. `data-no-drag` prevents
            the FAB drag hook from claiming the tap. */}
        {frameless && (
          <div
            className={`absolute inset-0 z-20 pointer-events-none transition-opacity duration-200 ${overlayVisible ? "opacity-100" : "opacity-0"}`}
            data-testid="mini-player-frameless-overlay"
          >
            {/* Double-tap flash — shows ⏪ or ⏩ on the half that was
                double-tapped. Pure visual feedback so the user sees
                the skip register. Auto-clears via flashTimerRef. */}
            {flashSide && (
              <div
                className={`absolute inset-y-0 w-1/2 grid place-items-center pointer-events-none animate-pulse ${
                  flashSide === "left" ? "left-0" : "right-0"
                }`}
                style={{ background: "radial-gradient(circle at center, rgba(0,0,0,0.55), transparent 70%)" }}
                data-testid={`mini-player-flash-${flashSide}`}
              >
                <div className="flex items-center gap-1 text-cream text-[11px] font-bold tracking-[0.18em] uppercase">
                  {flashSide === "left" ? <SkipBack size={22} weight="fill" /> : <SkipForward size={22} weight="fill" />}
                  <span>10s</span>
                </div>
              </div>
            )}

            {/* MINIMAL OVERLAY (per user spec) — only 5 controls, all
                tap-to-reveal, nothing else:
                  Top-right : ⛶ Expand/Restore · ✕ Close
                  Center    : ⏪ Rewind 10s · ▶/⏸ Play/Pause · ⏩ Forward 10s */}

            {/* Top-right: Return. Takes the user back to the page they
                were watching on (e.g. My Lineup) and resumes from the
                exact same spot, then closes the floating player. */}
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                const t = (() => {
                  try { if (videoRef.current && Number.isFinite(videoRef.current.currentTime)) return videoRef.current.currentTime; } catch (_) {}
                  return ytCurrentTimeRef.current || 0;
                })();
                const target = fromPath || "/";
                try {
                  sessionStorage.setItem("hw:lineup:resume", JSON.stringify({
                    id: item?.id, youtube_id: item?.youtube_id, t, ts: Date.now(),
                  }));
                } catch (_) {}
                close();
                try { navigate(target); } catch (_) {}
              }}
              onPointerDown={(e) => e.stopPropagation()}
              data-testid="mini-player-frameless-expand"
              data-no-drag="1"
              aria-label="Return to the page and resume"
              title="Return to page"
              style={{ touchAction: "manipulation", filter: "drop-shadow(0 1px 3px rgba(0,0,0,0.7))" }}
              className="pointer-events-auto absolute top-2 right-12 w-9 h-9 grid place-items-center text-cream/95 hover:text-white active:scale-95"
            >
              <ArrowsOut size={22} weight="bold" />
            </button>

            {/* Top-right: Close. */}
            <button
              type="button"
              onClick={(e) => { e.stopPropagation(); close(); }}
              onPointerDown={(e) => e.stopPropagation()}
              data-testid="mini-player-frameless-close"
              data-no-drag="1"
              aria-label="Close mini player"
              title="Close"
              style={{ touchAction: "manipulation", filter: "drop-shadow(0 1px 3px rgba(0,0,0,0.7))" }}
              className="pointer-events-auto absolute top-2 right-2 w-9 h-9 grid place-items-center text-cream/95 hover:text-white active:scale-95"
            >
              <X size={22} weight="bold" />
            </button>

            {/* Center: Rewind · Play/Pause · Forward. */}
            <div className="absolute inset-0 flex items-center justify-center gap-7 pointer-events-none">
              <button
                type="button"
                onClick={(e) => { e.stopPropagation(); skipBy(-10); showOverlay(); }}
                onPointerDown={(e) => e.stopPropagation()}
                data-testid="mini-player-frameless-rewind"
                data-no-drag="1"
                aria-label="Rewind 10 seconds"
                title="Rewind 10s"
                style={{ touchAction: "manipulation", filter: "drop-shadow(0 1px 3px rgba(0,0,0,0.7))" }}
                className="pointer-events-auto w-10 h-10 grid place-items-center text-cream/95 hover:text-white active:scale-95"
              >
                <SkipBack size={expanded ? 30 : 24} weight="fill" />
              </button>
              <button
                type="button"
                onClick={(e) => { e.stopPropagation(); togglePlayPause(); showOverlay(); }}
                onPointerDown={(e) => e.stopPropagation()}
                data-testid="mini-player-frameless-play"
                data-no-drag="1"
                aria-label={paused ? "Play" : "Pause"}
                title={paused ? "Play" : "Pause"}
                style={{ touchAction: "manipulation", filter: "drop-shadow(0 1px 3px rgba(0,0,0,0.7))" }}
                className="pointer-events-auto w-12 h-12 grid place-items-center text-cream/95 hover:text-white active:scale-95"
              >
                {paused ? <Play size={expanded ? 40 : 32} weight="fill" /> : <Pause size={expanded ? 40 : 32} weight="fill" />}
              </button>
              <button
                type="button"
                onClick={(e) => { e.stopPropagation(); skipBy(10); showOverlay(); }}
                onPointerDown={(e) => e.stopPropagation()}
                data-testid="mini-player-frameless-forward"
                data-no-drag="1"
                aria-label="Forward 10 seconds"
                title="Forward 10s"
                style={{ touchAction: "manipulation", filter: "drop-shadow(0 1px 3px rgba(0,0,0,0.7))" }}
                className="pointer-events-auto w-10 h-10 grid place-items-center text-cream/95 hover:text-white active:scale-95"
              >
                <SkipForward size={expanded ? 30 : 24} weight="fill" />
              </button>
            </div>
          </div>
        )}
        </div>
      </div>

      {/* Listen-mode placeholder — shown when user has tapped the
          music note. Audio keeps playing in the background; we show a
          tidy album-art-style card so the floating widget isn't a
          black rectangle. */}
      {listenMode && isHtmlVideo && (
        <div className="aspect-video bg-gradient-to-br from-forest via-forest/90 to-terracotta/40 grid place-items-center text-cream relative overflow-hidden">
          {item.poster && (
            <img src={item.poster} alt="" className="absolute inset-0 w-full h-full object-cover opacity-30" />
          )}
          <div className="relative text-center px-4">
            <MusicNote size={28} weight="duotone" className="mx-auto opacity-80" />
            <div className="text-[10px] tracking-[0.18em] uppercase font-bold mt-2 opacity-95">Listening</div>
            <div className="text-[9px] opacity-70 mt-0.5">Audio only · keeps playing in background</div>
          </div>
        </div>
      )}

      {/* Big "Back to [section]" bar — full-width, terracotta, sits
          right under the video so the user can't miss it. Tapping
          jumps them back to the page they opened the player from
          (Movies / Videos / Cartoons / Gospel / Kids / etc.) so they
          can pick another title without scrolling back. Only shown
          when:
            • `fromPath` was captured (always true once the player
              opened from a page); AND
            • The user is currently on a DIFFERENT page than the one
              they opened the player from — otherwise the button
              would just refresh the same page they're looking at. */}
      {fromPath && currentPath !== fromPath.split("?")[0] && !frameless && (
        <button
          type="button"
          onClick={(e) => { e.stopPropagation(); navigate(fromPath); }}
          onPointerDown={(e) => e.stopPropagation()}
          onMouseDown={(e) => e.stopPropagation()}
          onTouchStart={(e) => e.stopPropagation()}
          data-testid="mini-player-return-bar"
          data-no-drag="1"
          aria-label={`Back to ${labelForPath(fromPath)}`}
          title={`Back to ${labelForPath(fromPath)} — pick another title without scrolling`}
          className="w-full px-3 py-3 bg-gradient-to-r from-terracotta to-terracotta-600 hover:from-terracotta-600 hover:to-terracotta text-cream inline-flex items-center justify-center gap-2 transition-all border-t-2 border-cream/15"
        >
          <ArrowUUpLeft size={16} weight="bold" />
          <span className="text-[12px] tracking-[0.22em] uppercase font-bold truncate">
            Back to {labelForPath(fromPath)}
          </span>
        </button>
      )}

      {/* Drag-handle + Hide-to-edge footer. Big, obvious tap targets
          so even mobile users with chunky fingers can hit either one.
          Hidden in frameless mode — the user wants just the screen. */}
      {!frameless && (
      <div className="flex items-stretch w-full hw-player-deep select-none">
        <div className="flex-1 px-3 py-3 text-[11px] tracking-[0.18em] uppercase text-cream/95 text-center cursor-grab active:cursor-grabbing inline-flex items-center justify-center gap-2">
          <DotsSixVertical size={14} weight="bold" />
          <span className="font-bold">Drag to move</span>
        </div>
        <button
          type="button"
          onClick={(e) => { e.stopPropagation(); collapseTo(true); }}
          onPointerDown={(e) => e.stopPropagation()}
          onMouseDown={(e) => e.stopPropagation()}
          onTouchStart={(e) => e.stopPropagation()}
          data-testid="mini-player-hide"
          data-no-drag="1"
          aria-label="Hide to edge"
          title="Tuck against the edge — keeps playing"
          className="shrink-0 px-4 py-3 text-[11px] tracking-[0.18em] uppercase font-bold bg-terracotta hover:bg-terracotta/90 text-cream inline-flex items-center justify-center gap-1.5 transition-all"
        >
          {tuckedEdge === "left" && <CaretLeft size={14} weight="bold" />}
          {tuckedEdge === "right" && <CaretRight size={14} weight="bold" />}
          {tuckedEdge === "top" && <CaretUp size={14} weight="bold" />}
          {tuckedEdge === "bottom" && <CaretDown size={14} weight="bold" />}
          <span>Hide</span>
        </button>
      </div>
      )}
    </div>
  );
}


// Map a `fromPath` like "/movies?genre=drama" to a short, friendly
// label so the mini-player's return button can read "Back to Movies"
// instead of an opaque arrow icon. Pulled out into its own function
// because the same mapping is useful elsewhere (back-button overlays,
// breadcrumbs).
function labelForPath(p) {
  if (!p) return "Back";
  const path = p.split("?")[0];
  if (path.startsWith("/movies/black-american")) return "Black American films";
  if (path.startsWith("/movies")) return "Movies";
  if (path.startsWith("/videos")) return "Videos";
  if (path.startsWith("/cartoons")) return "Cartoons";
  if (path.startsWith("/kids")) return "Kids";
  if (path.startsWith("/gospel-music")) return "Gospel";
  if (path.startsWith("/sermons")) return "Sermons";
  if (path.startsWith("/devotional")) return "Devotional";
  if (path.startsWith("/storybook")) return "Picture Bible";
  if (path.startsWith("/read") || path.startsWith("/bible")) return "Bible";
  if (path === "/" || path === "") return "Home";
  // Fallback: take the first path segment, capitalize it.
  const seg = path.replace(/^\//, "").split("/")[0];
  return seg ? seg.charAt(0).toUpperCase() + seg.slice(1) : "Back";
}
